// body#checkout

import Big from 'big.js';
import axios from 'smg/axios';
import * as buttons from 'smg/buttons';
import { IsGSASmartPay } from 'smg/cc';
import * as countries from 'smg/countries';
import * as smgCarts from 'smg/customer-api/v2/carts';
import * as models from 'smg/customer-api/models';
import { CreateOrderPayments, OrderPaymentsCreateInit } from 'smg/customer-api/orderpayments';
import * as orders from 'smg/customer-api/orders';
import * as paymentoptions from 'smg/customer-api/paymentoptions';
import * as paypal from 'smg/customer-api/paypal';
import * as session from 'smg/customer-api/session';
import * as errors from 'smg/errors';
import { ReportValidity, SelectedNumber } from 'smg/form';
import * as phone from 'smg/phone';
import * as purchasetracking from 'smg/purchasetracking';
import QueryString from 'smg/querystring';
import { MountStaffNudge } from 'smg/staff-nudge';
import * as template from 'smg/template';
import { Uploader } from 'smg/uploads';
import { GetFirstAndLast } from 'smg/users';
import { Debounce, DollarFormat } from 'smg/util';
import { UUID } from 'smg/uuid';
import * as carts from '../carts';
import { Relref } from '../hugo';
import { ClearCardFieldInvalid, MarkCardFieldInvalid, PaymentOptionFields } from '../payments';
import * as blockchyp from 'smg/blockchyp';
import * as shippingoptions from '../shippingoptions';

void main();

declare interface myWindow extends Window {
    readonly staxOmniWebPaymentsToken: string;
}
declare const window: myWindow;

async function main() {
    const quoteID = QueryString().get('quote');
    const reorderID = QueryString().get('order');
    const cartID = QueryString().get('cart');

    if (quoteID) {
        await carts.AddCartItem({ quoteID }, {
            promptToCheckout: false,
            hideCartLink: true,
            showToast: false,
            newCart: true,
        }).catch(() => {
            window.location.href = '/';
        });
    } else if (reorderID) {
        await carts.AddCartItem({ orderID: reorderID }, {
            promptToCheckout: false,
            hideCartLink: true,
            showToast: false,
            newCart: true,
        }).catch(() => {
            window.location.href = '/';
        });
    } else if (cartID) {
        await carts.AddCartItem({ cartID }, {
            promptToCheckout: false,
            newCart: true,
        }).catch(() => {
            window.location.href = '/';
        });
    }

    const shippingOption: models.IShippingOption & { address: models.IAddress } = { address: {} };
    let paymentOption: models.IPaymentOption | undefined;

    const form = document.querySelector('#checkout-form') as HTMLFormElement;
    const fieldset = document.querySelector('#checkout-fieldset') as HTMLFieldSetElement;
    const placeOrder = document.querySelector('#checkout-button') as HTMLButtonElement;
    const gsaSmartPayWarning = document.querySelector('#gsa-smartpay-warning') as HTMLElement;
    const gsaSmartPayTaxAmount = gsaSmartPayWarning.querySelector('.gsa-smartpay-tax-amount') as HTMLElement;
    const checkoutError = document.querySelector('#checkout-error') as HTMLElement;
    const shippingFieldset = document.querySelector('#shipping-fieldset') as HTMLFieldSetElement;
    const sidebarFieldset = document.querySelector('#sidebar-fieldset') as HTMLFieldSetElement;

    // Shipping elements
    const shippingFullName = document.querySelector('#shipping-full-name') as HTMLInputElement;
    const shippingCompany = document.querySelector('#shipping-company') as HTMLInputElement;
    const shippingAddressLine1 = document.querySelector('#shipping-address-line-1') as HTMLInputElement;
    const shippingAddressLine2 = document.querySelector('#shipping-address-line-2') as HTMLInputElement;
    const shippingCity = document.querySelector('#shipping-city') as HTMLInputElement;
    const shippingZip = document.querySelector('#shipping-zip') as HTMLInputElement;
    const shippingCountry = document.querySelector('#shipping-country') as HTMLSelectElement;
    const shippingState = document.querySelector('#shipping-state') as HTMLSelectElement;
    const shippingPhoneEl = document.querySelector('#shipping-phone') as HTMLInputElement;
    const shippingPhone = new phone.Phone(shippingPhoneEl);
    const shippingEmail = document.querySelector('#shipping-email') as HTMLInputElement;

    // Payment card — clone the inline template
    const paymentCard = document.querySelector('#payment-card') as HTMLElement;
    const paymentEl = template.Clone('.order-payment-template').querySelector('.card-body') as HTMLFieldSetElement;

    const suffix = `-${UUID()}`;
    paymentEl.querySelectorAll('[id]:not([id=""])').forEach(el => {
        el.classList.add(el.id);
        el.id = `${el.id}${suffix}`;
    });
    paymentEl.querySelectorAll('label[for]:not([for=""])').forEach(el => el.setAttribute('for', `${el.getAttribute('for') || ''}${suffix}`));
    paymentEl.querySelectorAll('input[type="radio"][name]:not([name=""])').forEach(el => el.setAttribute('name', `${el.getAttribute('name') || ''}${suffix}`));

    paymentCard.appendChild(paymentEl);

    const billingForm = paymentEl.querySelector('.billing-form') as HTMLFieldSetElement;
    const paymentOptionContainer = paymentEl.querySelector('.payment-option') as HTMLElement;
    const paymentError = paymentEl.querySelector('.payment-error') as HTMLElement;
    const paymentErrorText = paymentError.querySelector('.alert-text') as HTMLElement;

    // Hoist order state across submit retries so a post-create failure
    // (Stax exchange, /order-payments) does not create a second order (#8111).
    // orderID is also read by resolveMerchantToken after create (order path).
    // Empty-string defaults keep resolveMerchantToken safe before create.
    let orderID = '';
    let orderKey = '';
    // Hoisted with orderID: a submit can throw AFTER the order exists but
    // BEFORE /checkout-complete fires (merchant-parity remount, a 422 on the
    // Stax exchange). Gating the POST on "created this submit" would skip it
    // on every retry and NodeOrderCheckoutComplete flows would never run for
    // the order. Set only after a successful POST — at-least-once, since the
    // handler fires the node event unconditionally and a lost event is worse
    // than a rare duplicate from a response that failed after the server
    // processed it.
    let checkoutCompleteSent = false;
    let shippingOptionID = '';
    let charged = false;
    let payIdemKey: string | undefined;

    // The order this cart becomes may be re-homed to the company that owns the
    // cart's source quote/order (see orders.go), and the BlockChyp tokenizing key
    // is per-merchant — so mount under the cart's resolved merchant, not the
    // browsing site's. No fallback: a mis-keyed token fails the exchange after the
    // order exists, which is unrecoverable. Lazily resolved and shared by every
    // Mount so mountBlockChyp stays synchronous. Cleared on rejection (empty
    // token, tokenizing-key lookup failure, or iframe load failure) so a later
    // mount/retry re-resolves instead of reusing a dead promise.
    //
    // Once orderID is set (post-/orders), resolve by orderIDs instead of cart —
    // the cart is forgotten after create, and normalizeNewOrderRealmToSalesTeam
    // (#7537) may have re-homed the order to a different merchant than the cart
    // resolved (see merchant check after createOrder).
    let merchantToken: Promise<string> | undefined;
    const resolveMerchantToken = (): Promise<string> => {
        if (!merchantToken) {
            const merchantLookup = orderID ?
                paymentoptions.PaymentMerchant({ orderIDs: [orderID] }) :
                paymentoptions.PaymentMerchant({ cartID: smgCarts.GetCartID() || '' });
            const pending: Promise<string> = merchantLookup.
                then(merchant => {
                    if (!merchant.webPaymentsToken) {
                        throw new Error('This brand is missing its payment processor configuration, so the card cannot be entered. Please contact us to complete your order.');
                    }
                    return merchant.webPaymentsToken;
                });
            merchantToken = pending;
            pending.catch(() => {
                // Clear the cache only if it still holds THIS promise — Start
                // Over (or the parity remount) may already have replaced it
                // with a newer lookup, and a stale rejection clearing that one
                // would drop live token provenance and force a needless
                // re-enter-your-card cycle.
                if (merchantToken === pending) {
                    merchantToken = undefined;
                }
            });
        }
        return merchantToken;
    };

    const paymentOptionFields = new PaymentOptionFields(
        paymentEl.querySelector('.address-fields') as HTMLFieldSetElement,
        paymentEl.querySelector('.same-as-shipping') as HTMLElement,
        paymentEl.querySelector('.billing-same-as-shipping') as HTMLInputElement,
        paymentEl.querySelector('.billing-full-name') as HTMLInputElement,
        paymentEl.querySelector('.billing-company') as HTMLInputElement,
        paymentEl.querySelector('.billing-phone') as HTMLInputElement,
        paymentEl.querySelector('.billing-email') as HTMLInputElement,
        paymentEl.querySelector('.billing-address-line-1') as HTMLInputElement,
        paymentEl.querySelector('.billing-address-line-2') as HTMLInputElement,
        paymentEl.querySelector('.billing-city') as HTMLInputElement,
        paymentEl.querySelector('.billing-state') as HTMLSelectElement,
        paymentEl.querySelector('.billing-zip') as HTMLInputElement,
        paymentEl.querySelector('.billing-country') as HTMLSelectElement,
        paymentEl.querySelector('.billing-payment-option-type-parent') as HTMLElement,
        paymentEl.querySelector('.billing-payment-option-type') as HTMLSelectElement,
        paymentEl.querySelector('.fields-cc') as HTMLFieldSetElement,
        paymentEl.querySelector('.fields-bank') as HTMLFieldSetElement,
        paymentEl.querySelector('.fields-paypal') as HTMLFieldSetElement,
        paymentEl.querySelector('.billing-credit-card-number') as HTMLElement,
        paymentEl.querySelector('.billing-credit-card-month') as HTMLSelectElement,
        paymentEl.querySelector('.billing-credit-card-year') as HTMLSelectElement,
        paymentEl.querySelector('.billing-credit-card-security') as HTMLElement,
        paymentEl.querySelector('.billing-account-number') as HTMLInputElement,
        paymentEl.querySelector('.billing-routing-number') as HTMLInputElement,
        paymentEl.querySelector('.billing-account-type-checking') as HTMLInputElement,
        paymentEl.querySelector('.billing-account-type-savings') as HTMLInputElement,
        paymentEl.querySelector('.billing-bank-type-personal') as HTMLInputElement,
        paymentEl.querySelector('.billing-bank-type-business') as HTMLInputElement,
        paymentEl.querySelector('.fields-po') as HTMLFieldSetElement,
        paymentEl.querySelector('.billing-po-number') as HTMLInputElement,
        paymentEl.querySelector('.billing-po-uploads') as HTMLLabelElement,
        paymentEl.querySelector('.billing-po-uploads-thumbnails') as HTMLElement,
        undefined,
        resolveMerchantToken,
    );

    // BlockChyp iframes are cross-origin and can't be `disabled` from our
    // side. Without this the customer can edit the card after the token that
    // will actually be charged has already been minted (tokenize card A, swap
    // to card B mid-flight → charge A while the UI shows B).
    const freezeCardIframes = () => {
        for (const el of [paymentOptionFields.$card, paymentOptionFields.$security]) {
            el.style.pointerEvents = 'none';
            el.style.opacity = '0.6';
            el.querySelectorAll('iframe').forEach(iframe => iframe.setAttribute('tabindex', '-1'));
        }
    };
    const thawCardIframes = () => {
        for (const el of [paymentOptionFields.$card, paymentOptionFields.$security]) {
            el.style.pointerEvents = '';
            el.style.opacity = '';
            el.querySelectorAll('iframe').forEach(iframe => iframe.removeAttribute('tabindex'));
        }
    };

    // PO file uploader
    const poUploader = new Uploader(paymentOptionFields.$poUploads, {
        thumbnails: paymentOptionFields.$poUploadsThumbnails,
        thumbnailMaxHeight: '100px',
    });

    // Track payment option type from the inline form
    paymentOptionFields.$paymentOptionType.addEventListener('change', () => {
        if (paymentOption) {
            const paymentOptionTypeID = parseInt(paymentOptionFields.$paymentOptionType.value, 10);
            if (models.IsPaymentOptionTypeID(paymentOptionTypeID)) {
                paymentOption.paymentOptionTypeID = paymentOptionTypeID;
            }
        }
        updateGSASmartPayWarning();
    });

    // Wire up billing field inputs to track payment option data
    paymentOptionFields.$fullName.addEventListener('input', () => {
        if (paymentOption) {
            paymentOption.fullName = paymentOptionFields.$fullName.value;
        }
    });
    paymentOptionFields.$company.addEventListener('input', () => {
        if (paymentOption) {
            paymentOption.company = paymentOptionFields.$company.value;
        }
    });
    paymentOptionFields.$addressLine1.addEventListener('input', () => {
        if (paymentOption) {
            paymentOption.addressLine1 = paymentOptionFields.$addressLine1.value;
        }
    });
    paymentOptionFields.$addressLine2.addEventListener('input', () => {
        if (paymentOption) {
            paymentOption.addressLine2 = paymentOptionFields.$addressLine2.value;
        }
    });
    paymentOptionFields.$city.addEventListener('input', () => {
        if (paymentOption) {
            paymentOption.city = paymentOptionFields.$city.value;
        }
    });
    paymentOptionFields.$zip.addEventListener('input', () => {
        if (paymentOption) {
            paymentOption.zip = paymentOptionFields.$zip.value;
        }
    });
    paymentOptionFields.$phone.addEventListener('input', async () => {
        if (paymentOption) {
            paymentOption.phone = await paymentOptionFields.phone.ready().then(() => paymentOptionFields.phone.format());
        }
    });
    paymentOptionFields.$mail.addEventListener('input', () => {
        if (paymentOption) {
            paymentOption.email = paymentOptionFields.$mail.value;
        }
    });

    // Any field edit forces createPaymentOption to run again on the next submit.
    // Clear BOTH id and key: the submit guard (`!id && !key`) and /order-payments
    // (`key || id`) both key off `key`, so dropping only `id` would silently
    // reuse a stale tokenized option instead of re-tokenizing the new input.
    const invalidateSavedPaymentOption = () => {
        delete paymentOption?.id;
        delete paymentOption?.key;
    };

    [
        paymentOptionFields.$fullName,
        paymentOptionFields.$company,
        paymentOptionFields.$addressLine1,
        paymentOptionFields.$addressLine2,
        paymentOptionFields.$city,
        paymentOptionFields.$zip,
        paymentOptionFields.$phone,
        paymentOptionFields.$mail,
        paymentOptionFields.$accountNumber,
        paymentOptionFields.$routingNumber,
    ].forEach(el => el.addEventListener('input', invalidateSavedPaymentOption));

    // PAN/CVC live inside the BlockChyp iframe and don't bubble input events to
    // our page. BlockChyp's cardType (while typing) and fieldEvent (focus/blur)
    // signal "user touched the iframe" so editing the card after a decline
    // re-tokenizes instead of reusing the stored payment option.
    paymentOptionFields.onCardInput = () => {
        invalidateSavedPaymentOption();
        // Editing the card clears any painted field error from a
        // prior tokenize failure.
        ClearCardFieldInvalid(paymentOptionFields);
    };

    [
        paymentOptionFields.$paymentOptionType,
        paymentOptionFields.$country,
        paymentOptionFields.$state,
        paymentOptionFields.$month,
        paymentOptionFields.$year,
    ].forEach(el => el.addEventListener('change', invalidateSavedPaymentOption));

    [
        paymentOptionFields.$bankChecking,
        paymentOptionFields.$bankSavings,
        paymentOptionFields.$bankPersonal,
        paymentOptionFields.$bankBusiness,
    ].forEach(el => el.addEventListener('click', invalidateSavedPaymentOption));

    // Hydrate country/state codes from IDs onto a real PaymentOption.
    const updateBillingCountryAndState = async (paymentOptionInit: models.IPaymentOption): Promise<models.PaymentOption> => {
        const paymentOption = new models.PaymentOption(paymentOptionInit);
        paymentOption.country = undefined;
        paymentOption.state = undefined;

        if (paymentOption.countryID) {
            const country = await countries.GetCountry(paymentOption.countryID);
            if (country) {
                paymentOption.country = new models.Country(country);
                if (paymentOption.stateID) {
                    const state = await countries.GetState(paymentOption.countryID, paymentOption.stateID);
                    if (state) {
                        paymentOption.state = new models.State(state);
                    }
                }
            }
        }
        return paymentOption;
    };

    // Initialize payment option type from the form default
    paymentOption = {
        paymentOptionTypeID: (() => {
            const paymentOptionTypeID = parseInt(paymentOptionFields.$paymentOptionType.value, 10);
            return models.IsPaymentOptionTypeID(paymentOptionTypeID) ? paymentOptionTypeID : models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_NONE;
        })(),
    };

    const billingPhoneInput = paymentOptionFields.phone;

    // BlockChyp token + billing fields gathered before the order exists so a
    // bad card fails without orphaning an order. NewPaymentOption (Stax
    // merchant exchange) runs after createOrder with orderIDs (#8111).
    let pendingPaymentOptionCreate: paymentoptions.PendingPaymentOptionCreate | undefined;

    // Card-entry prep from inline form values (no Stax / persist yet).
    const preparePaymentOption = async (): Promise<void> => {
        const sameAsShipping = paymentOptionFields.$sameAsShipping.checked;
        const po = await updateBillingCountryAndState({
            fullName: sameAsShipping ? shippingOption.address.fullName : paymentOptionFields.$fullName.value,
            company: sameAsShipping ? shippingOption.address.organization : paymentOptionFields.$company.value,
            countryID: sameAsShipping ? shippingOption.address.countryID : SelectedNumber(paymentOptionFields.$country),
            addressLine1: sameAsShipping ? shippingOption.address.addressLine1 : paymentOptionFields.$addressLine1.value,
            addressLine2: sameAsShipping ? shippingOption.address.addressLine2 : paymentOptionFields.$addressLine2.value,
            city: sameAsShipping ? shippingOption.address.city : paymentOptionFields.$city.value,
            stateID: sameAsShipping ? shippingOption.address.stateID : SelectedNumber(paymentOptionFields.$state),
            phone: sameAsShipping ? await shippingPhone.format() : await billingPhoneInput.format(),
            zip: sameAsShipping ? shippingOption.address.postalCode : paymentOptionFields.$zip.value,
            email: sameAsShipping ? shippingOption.address.emailAddress?.email : paymentOptionFields.$mail.value,
            paymentOptionTypeID: paymentOption?.paymentOptionTypeID,
        });

        let paymentToken: string | undefined;
        let creditCardFirst6: string | undefined;
        let creditCardLast4: string | undefined;
        if (po.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT) {
            // Tokenize the PAN/CVC via the BlockChyp iframe before the order
            // exists so a bad card fails without orphaning an order. Pass the
            // normalized billing fields so the token metadata matches Stax.
            try {
                const tokenized = await paymentOptionFields.tokenize({
                    fullName: po.fullName || '',
                    month: parseInt(paymentOptionFields.$month.value, 10),
                    year: parseInt(paymentOptionFields.$year.value, 10),
                    addressLine1: po.addressLine1 || '',
                    addressLine2: po.addressLine2 || '',
                    city: po.city || '',
                    stateCode: po.state?.stateCode,
                    zip: po.zip || '',
                    countryCode3: po.country?.countryCode3,
                    company: po.company,
                });
                paymentToken = tokenized.token;
                if (tokenized.firstSix) {
                    creditCardFirst6 = tokenized.firstSix;
                }
                if (tokenized.lastFour) {
                    creditCardLast4 = tokenized.lastFour;
                }
            } catch (err) {
                // A tokenize timeout retires its mount (ts/blockchyp.ts
                // removes the frames so a straggling response can't settle
                // a later attempt), so rebuild for the retry.
                if (paymentOptionFields.mount?.isUnloaded) {
                    paymentOptionFields.mountBlockChyp();
                }
                thawCardIframes();
                if (err instanceof blockchyp.TokenizeFieldError) {
                    MarkCardFieldInvalid(paymentOptionFields, err.field, err.message);
                }
                throw err;
            }
        }

        pendingPaymentOptionCreate = {
            paymentOption: po,
            paymentToken,
            creditCardFirst6,
            creditCardLast4,
            expireMonth: parseInt(paymentOptionFields.$month.value, 10),
            expireYear: parseInt(paymentOptionFields.$year.value, 10),
        };
    };

    // Show saved payment option summary with "Change Method" link
    const drawSavedPaymentOption = (paymentOptionInit: models.IPaymentOption) => {
        const po = new models.PaymentOption(paymentOptionInit);
        billingForm.classList.add('d-none');
        billingForm.disabled = true;
        paymentOptionContainer.classList.remove('d-none');

        let label = 'Payment method selected';
        if (po.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT) {
            label = `Credit card ending in ${po.creditCardLast4 || '****'}`;
        } else if (po.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK) {
            label = 'Bank account';
            if (po.creditCardLast4) {
                label += ` ending in ${po.creditCardLast4}`;
            }
        }

        paymentOptionContainer.innerHTML = `
            <div class="alert alert-success mb-3">
                <i class="fas fa-check-circle me-1"></i> ${label}
                <a href="#" class="change-payment-method ms-2">Change Method</a>
            </div>
        `;

        const changeLink = paymentOptionContainer.querySelector('.change-payment-method') as HTMLAnchorElement;
        changeLink.addEventListener('click', e => {
            e.preventDefault();
            billingForm.classList.remove('d-none');
            billingForm.disabled = false;
            paymentOptionContainer.classList.add('d-none');
            invalidateSavedPaymentOption();
        });
    };

    const showPaymentError = (msg: string) => {
        paymentError.classList.remove('d-none');
        paymentErrorText.textContent = msg;
    };

    const hidePaymentError = () => {
        paymentError.classList.add('d-none');
    };

    // Sidebar elements
    const cartSubtotalEl = document.querySelector('.cart-subtotal') as HTMLElement;
    const cartAvailableDiscountEl = document.querySelector('.cart-available-discount') as HTMLElement;
    const cartAvailableDiscountParent = document.querySelector('.cart-available-discount-parent') as HTMLElement;

    const cartShippingEl = document.querySelector('.cart-shipping') as HTMLElement;
    const cartShippingSpinner = document.querySelector('.cart-shipping-spinner') as HTMLElement;
    const cartTaxEl = document.querySelector('.cart-tax') as HTMLElement;
    const cartTaxRateParent = document.querySelector('.cart-tax-rate-parent') as HTMLElement;
    const cartTaxRateEl = document.querySelector('.cart-tax-rate') as HTMLElement;
    const cartTaxSpinner = document.querySelector('.cart-tax-spinner') as HTMLElement;
    const cartTotalEl = document.querySelector('.cart-total') as HTMLElement;

    const cartPrepaymentParent = document.querySelector('.cart-prepayment-parent') as HTMLElement;
    const cartPrepaymentEl = document.querySelector('.cart-prepayment') as HTMLElement;

    const prepaymentCard = document.querySelector('#prepayment-card') as HTMLElement;
    const prepaymentTotalEl = prepaymentCard.querySelector('#prepayment-total') as HTMLElement;
    const prepaymentListEl = prepaymentCard.querySelector('#prepayment-list') as HTMLElement;

    const caseStudy = document.querySelector('#case-study') as HTMLInputElement;
    const sms = document.querySelector('#sms-opt-in') as HTMLInputElement;

    // Totals
    let subtotal: Big = Big(0);
    let availableDiscount: Big = Big(0);
    let shipping: Big = Big(0);
    let taxAmount: Big = Big(0);
    let gsaSmartPayWarned = false;
    const gsaSmartPayAcknowledgedFirst6s = new Set<string>();
    let taxRate: Big = Big(0);
    let prepayment: Big = Big(0);
    let total: Big = Big(0);
    let cartItems: models.CartItem[] = [];

    // Country/state setup
    await countries.Selects(shippingCountry, shippingState, {
        defaultUnitedStates: true,
        stateParentSelector: 'div',
        phone: shippingPhone,
        onStateHide: () => {
            shippingState.disabled = true;
        },
        onStateShow: () => {
            shippingState.disabled = false;
        },
    });

    // Shipping input handlers
    shippingFullName.addEventListener('input', () => {
        shippingOption.address.fullName = shippingFullName.value;
    });
    shippingCompany.addEventListener('input', () => {
        shippingOption.address.organization = shippingCompany.value;
    });
    shippingAddressLine1.addEventListener('input', () => {
        shippingOption.address.addressLine1 = shippingAddressLine1.value;
        updateCartAddress();
    });
    shippingAddressLine2.addEventListener('input', () => {
        shippingOption.address.addressLine2 = shippingAddressLine2.value;
    });
    shippingCity.addEventListener('input', () => {
        shippingOption.address.city = shippingCity.value;
        updateCartAddress();
    });
    shippingZip.addEventListener('input', () => {
        shippingOption.address.postalCode = shippingZip.value;
        updateCartAddress();
    });
    shippingPhoneEl.addEventListener('input', async () => {
        shippingOption.address.phoneNumber = new models.PhoneNumber({ phone: await shippingPhone.ready().then(() => shippingPhone.format()) });
    });
    shippingEmail.addEventListener('input', () => {
        shippingOption.address.emailAddress = new models.EmailAddress({ email: shippingEmail.value });
    });

    shippingCountry.addEventListener('change', async () => {
        shippingOption.address.countryID = parseInt(shippingCountry.value, 10) as models.CountryID;
        shippingOption.address.state = undefined;
        shippingOption.address.stateID = models.StateID.STATE_ZERO;
        await updateCountryAndState();
        updateCartAddress();
    });

    shippingState.addEventListener('change', async () => {
        shippingOption.address.stateID = parseInt(shippingState.value, 10) as models.StateID;
        await updateCountryAndState();
        updateCartAddress();
    });

    async function updateCountryAndState() {
        shippingOption.address.country = undefined;
        shippingOption.address.state = undefined;

        if (shippingOption.address.countryID) {
            const country = await countries.GetCountry(shippingOption.address.countryID);
            if (country) {
                shippingOption.address.country = new models.Country(country);

                if (shippingOption.address.stateID) {
                    const state = await countries.GetState(shippingOption.address.countryID, shippingOption.address.stateID);
                    if (state) {
                        shippingOption.address.state = new models.State(state);
                    }
                }
            }
        }
    }

    // The server already caps the deposit credit against the cart's pre-credit
    // total, but re-cap here so a later shipping/tax recompute can never leave
    // the displayed total below $0 — the credit shown can't exceed what's owed.
    const effectivePrepayment = (): Big => {
        const preTotal = subtotal.add(availableDiscount).add(shipping).add(taxAmount);
        if (preTotal.lt(0)) {
            return Big(0);
        }
        return prepayment.gt(preTotal) ? preTotal : prepayment;
    };

    // Cart totals
    const updateCartTotals = () => {
        total = subtotal.add(availableDiscount).add(shipping).add(taxAmount).sub(effectivePrepayment());
    };

    // GSA first6 on the payment that would actually charge as a card right
    // now: a saved payment option carries its own type, while a not-yet-
    // created one follows the live type select — so switching to PayPal or
    // check/PO takes a leftover tokenized card out of play.
    const gsaSmartPayFirst6 = () => {
        if (paymentOption?.id || paymentOption?.key) {
            if (paymentOption.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT &&
                IsGSASmartPay(String(paymentOption.creditCardFirst6 || ''))) {
                return String(paymentOption.creditCardFirst6);
            }
            return '';
        }
        if (parseInt(paymentOptionFields.$paymentOptionType.value, 10) !== models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT) {
            return '';
        }
        const pendingFirst6 = pendingPaymentOptionCreate?.creditCardFirst6 || '';
        if (IsGSASmartPay(pendingFirst6)) {
            return pendingFirst6;
        }
        return '';
    };

    // The BIN is a hint, not proof (GSA travel cards share the purchase-card
    // prefixes), so this only ever warns — never blocks or auto-exempts (#3777).
    // gsaSmartPayWarned is set when the customer declines the place-order
    // prompt: the inline alert stays behind as context for why the order
    // didn't go through. This checkout has no tax-exemption input, so the
    // alert points at contacting us instead.
    const updateGSASmartPayWarning = () => {
        gsaSmartPayTaxAmount.textContent = DollarFormat(taxAmount);
        gsaSmartPayWarning.classList.toggle('d-none', !(gsaSmartPayWarned && taxAmount.gt(0) && gsaSmartPayFirst6() !== ''));
    };

    const drawCartTotals = () => {
        cartSubtotalEl.textContent = DollarFormat(subtotal);
        cartShippingEl.textContent = DollarFormat(shipping);
        cartTaxEl.textContent = DollarFormat(taxAmount);

        if (!taxRate.eq(0)) {
            cartTaxRateParent.classList.remove('d-none');
            cartTaxRateEl.textContent = String(taxRate.mul('100'));
        } else {
            cartTaxRateParent.classList.add('d-none');
        }

        cartAvailableDiscountEl.textContent = DollarFormat(availableDiscount);
        cartAvailableDiscountParent.classList.toggle('d-none', availableDiscount.eq(0));

        // Render the credit as -$X.XX. Its cap moves with shipping/tax, so the
        // card's "Applied" header is repainted from the same value here too.
        const appliedPrepayment = effectivePrepayment();
        if (appliedPrepayment.gt(0)) {
            cartPrepaymentEl.textContent = DollarFormat(Big(0).sub(appliedPrepayment));
            cartPrepaymentParent.classList.remove('d-none');
        } else {
            cartPrepaymentParent.classList.add('d-none');
        }
        prepaymentTotalEl.textContent = DollarFormat(appliedPrepayment);

        cartTotalEl.textContent = DollarFormat(total);

        updateGSASmartPayWarning();
    };

    // Debounced cart recalculation on address change
    const updateCartDebounce = Debounce(async () => {
        let taxAddress: models.ICartTotalsAddress | undefined;
        if (
            shippingOption.address.countryID &&
            shippingOption.address.addressLine1 &&
            shippingOption.address.city &&
            (shippingOption.address.stateID || !(await countries.GetCountry(shippingOption.address.countryID))?.states?.length) &&
            shippingOption.address.postalCode && (shippingOption.address.postalCode.trim().length >= 5 || shippingOption.address.countryID !== models.CountryID.COUNTRIES_UNITED_STATES)
        ) {
            taxAddress = {
                addressLine1: shippingOption.address.addressLine1,
                addressLine2: shippingOption.address.addressLine2,
                city: shippingOption.address.city,
                countryID: shippingOption.address.countryID,
                stateID: shippingOption.address.stateID,
                zip: shippingOption.address.postalCode,
            };
        }

        smgCarts.GetCart('totals', { shippingAddress: taxAddress }).then(cart => {
            const totals = cart?.totals;
            shipping = totals?.shipping ?? Big(0);
            taxAmount = totals?.taxAmount ?? Big(0);
            taxRate = totals?.taxRate ?? Big(0);
            prepayment = totals?.prepayment ?? Big(0);
            availableDiscount = totals?.availableDiscount ?? Big(0);
        }).catch(err => {
            taxAmount = Big(0);
            taxRate = Big(0);

            errors.ShowError(err, form, undefined, { 'zip': 'shipping.zip' });
        }).finally(() => {
            updateCartTotals();
            drawCartTotals();
            cartTaxSpinner.classList.add('d-none');
            cartShippingSpinner.classList.add('d-none');
            placeOrder.disabled = false;
        });
    }, 500, false);

    const updateCartAddress = () => {
        cartTaxSpinner.classList.remove('d-none');
        cartShippingSpinner.classList.remove('d-none');
        placeOrder.disabled = true;
        updateCartDebounce();
    };

    const getUser = (): models.User => {
        const { firstName, lastName } = GetFirstAndLast(shippingOption.address.fullName || '');
        return new models.User({
            firstName,
            lastName,
            company: shippingOption.address.organization || '',
            email: shippingOption.address.emailAddress?.email || '',
            phone: shippingOption.address.phoneNumber?.phone || '',
            countryID: shippingOption.address.countryID,
            stateID: shippingOption.address.stateID,
        });
    };

    // Form submission
    form.noValidate = true;
    form.addEventListener('submit', e => {
        e.preventDefault();
    });

    const showLoading = () => {
        fieldset.disabled = true;
        buttons.Start(placeOrder);
    };

    const hideLoading = () => {
        fieldset.disabled = false;
        buttons.Stop(placeOrder);
    };

    const hideError = () => {
        checkoutError.classList.add('d-none');
    };

    placeOrder.addEventListener('click', async () => {
        hideError();
        hidePaymentError();

        // Re-sync the model from the DOM before validating/POSTing.
        // Browser form restoration (Safari/iOS tab restore) and some
        // autofill paths set input values without firing 'input',
        // leaving the event-synced model stale — the server then 400s
        // on a required field the customer can see is filled (#7955).
        shippingOption.address.fullName = shippingFullName.value;
        shippingOption.address.organization = shippingCompany.value;
        shippingOption.address.addressLine1 = shippingAddressLine1.value;
        shippingOption.address.addressLine2 = shippingAddressLine2.value;
        shippingOption.address.city = shippingCity.value;
        shippingOption.address.postalCode = shippingZip.value;
        shippingOption.address.emailAddress = new models.EmailAddress({ email: shippingEmail.value });

        // Validate zip for US
        if (shippingOption.address.countryID === models.CountryID.COUNTRIES_UNITED_STATES && (shippingOption.address.postalCode || '').trim().length < 5) {
            shippingZip.setCustomValidity('A valid zip code is required');
            const clearZipError = () => {
                shippingZip.setCustomValidity('');
                shippingZip.removeEventListener('input', clearZipError);
            };
            shippingZip.addEventListener('input', clearZipError);
        }

        if (!ReportValidity(form)) {
            return;
        }

        showLoading();

        try {
            // Card-entry prep before any order exists so a bad card fails
            // without orphaning an order. NewPaymentOption waits until after
            // createOrder with orderIDs (#8111). PayPal never mints Stax.
            const isPayPal = paymentOption?.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL;
            const needsNewPaymentOption = !isPayPal && !paymentOption?.id && !paymentOption?.key;
            const isCC = paymentOption?.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT;
            // Freeze iframe interaction for the CC tokenize window. The native
            // fieldset disable above doesn't affect cross-origin BlockChyp
            // iframes; without this freeze, the user could edit PAN/CVC AFTER
            // preparePaymentOption caches a token and the cached (stale) token
            // would still be what we charge.
            if (needsNewPaymentOption && isCC) {
                freezeCardIframes();
            }
            if (needsNewPaymentOption) {
                try {
                    await preparePaymentOption();
                } catch (err) {
                    if (paymentOptionFields.mount?.isUnloaded) {
                        paymentOptionFields.mountBlockChyp();
                    }
                    thawCardIframes();
                    hideLoading();
                    // Already painted on the offending card mount in prepare —
                    // no dialog on top of it.
                    if (err instanceof blockchyp.TokenizeFieldError) {
                        return;
                    }
                    showPaymentError(errors.GetErrorMessage(err));
                    return;
                }
            }

            // Prompt when a GSA SmartPay government card is about to pay
            // sales tax (#3777): tokenizing above is the first time a new
            // card's BIN is visible. Confirming continues this same
            // submission — nothing has been persisted yet, so declining
            // aborts safely and clicking Place Order again re-tokenizes
            // exactly like the payment-error path above (and re-prompts,
            // since only confirming acknowledges). This checkout has no
            // tax-exemption input, so the prompt's alternative is the
            // contact page (the inline alert's link).
            // Acknowledgment is per-BIN, so swapping to a different GSA card
            // prompts again.
            const gsaFirst6 = !isPayPal && total.gt(0) && taxAmount.gt(0) ? gsaSmartPayFirst6() : '';
            if (gsaFirst6 && !gsaSmartPayAcknowledgedFirst6s.has(gsaFirst6)) {
                const gsaSmartPay = await import('smg/gsa-smartpay');
                const choice = await gsaSmartPay.ConfirmGSASmartPay(DollarFormat(taxAmount), 'contact');
                if (choice !== 'continue') {
                    gsaSmartPayWarned = true;
                    updateGSASmartPayWarning();
                    if (needsNewPaymentOption && isCC) {
                        if (paymentOptionFields.mount?.isUnloaded) {
                            paymentOptionFields.mountBlockChyp();
                        }
                        thawCardIframes();
                    }
                    hideLoading();
                    if (choice === 'exemption') {
                        window.location.href = (gsaSmartPayWarning.querySelector('a') as HTMLAnchorElement).href;
                    } else {
                        gsaSmartPayWarning.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                    return;
                }
                gsaSmartPayAcknowledgedFirst6s.add(gsaFirst6);
            }

            // Create shipping option once — a retry must not create a duplicate row.
            if (!shippingOptionID) {
                shippingOption.address.phoneNumber = new models.PhoneNumber({ phone: await shippingPhone.format() });
                const newShippingOption = await shippingoptions.New(getUser(), shippingOption);
                shippingOptionID = newShippingOption.id;
            }

            // Create order once — a retry re-attempts only the charge.
            if (!orderID) {
                const orderResp = await orders.NewOrder({
                    'user': getUser(),
                    'cartID': smgCarts.GetCartID(),
                    'shippingOptionIDs': [shippingOptionID],
                    'sms': sms.checked,
                    'promotionalApproval': caseStudy.checked,
                });
                orderID = orderResp.orderID;
                orderKey = orderResp.key;

                purchasetracking.TrackPurchase({
                    invoiceNumber: orderResp.invoiceNumber.toString(),
                    orderID: orderResp.orderID,
                    value: parseFloat(total.toString()),
                    cartItems: cartItems,
                });

                // Lock what the created order has already consumed. These are nested
                // fieldsets, so they stay disabled when hideLoading re-enables the outer
                // #checkout-fieldset — the customer can still fix payment and click
                // Place Order again.
                shippingFieldset.disabled = true;
                sidebarFieldset.disabled = true;
            }

            // normalizeNewOrderRealmToSalesTeam (#7537) can re-home a *new*
            // order onto the sales team's primary realm when that team does not
            // serve the cart's realm. The BlockChyp tokenizing key is
            // per-merchant, so tokens minted under the cart's merchant cannot
            // be exchanged under the order's merchant. Mounts were built from
            // the cart-resolved token before /orders; after create, compare
            // with the order's merchant and fail early + remount rather than a
            // cryptic exchange error (same fail-loudly posture as the prepay
            // residual in go/lambda/customer-api/orders.go — do not re-home
            // money server-side). PaymentMerchant is memoized; NewPaymentOption
            // makes the identical orderIDs call moments later, so no extra RTT.
            //
            // Runs on EVERY submit that still carries a pending token, not just
            // the click that created the order: if this lookup transiently
            // fails, the outer catch surfaces it and the retry must re-check —
            // the retry re-tokenizes under the still-cached cart-merchant
            // mount, so skipping the comparison would keep minting tokens the
            // order's merchant can never exchange.
            //
            // Read the CACHED promise, never resolveMerchantToken() — orderID is
            // set by now, so that helper would resolve by orderIDs and compare
            // the order's merchant against itself, which can never mismatch.
            // Only run for CC payments that already tokenized (paymentToken set).
            if (pendingPaymentOptionCreate?.paymentToken) {
                const orderMerchant = await paymentoptions.PaymentMerchant({ orderIDs: [orderID] });
                const mountToken = await merchantToken?.catch(() => undefined);
                // Fail CLOSED on missing provenance: a pending token always came
                // from a mount, and every mount caches merchantToken — so an
                // undefined mountToken here means the cache was cleared after
                // tokenizing (lost provenance), not "never mounted". Treat it
                // exactly like a mismatch rather than skipping the check.
                if (orderMerchant.webPaymentsToken && orderMerchant.webPaymentsToken !== mountToken) {
                    pendingPaymentOptionCreate = undefined;
                    merchantToken = undefined;
                    paymentOptionFields.unloadBlockChyp();
                    paymentOptionFields.mountBlockChyp();
                    thawCardIframes();
                    hideLoading();
                    showPaymentError('Your order was placed, but the card needs to be re-entered to complete payment. Please re-enter your card details and click Place Order again.');
                    return;
                }
            }

            // PayPal flow
            if (isPayPal) {
                const successURL = `${window.location.origin}${Relref('/order-success')}?id=${orderKey}&deleteCart=true`;
                // Cancelling returns to the order that was already created, not
                // to checkout: a reload there loses the in-memory orderID, so
                // the next submit would place a SECOND order for the same cart
                // and strand the first.
                const cancelURL = `${window.location.origin}${Relref('/orders/make-order-payment')}?order=${orderKey}`;

                const payPalURL = await paypal.CreateOrder({
                    user: getUser(),
                    orderIDs: [orderID],
                    successURL,
                    cancelURL,
                });

                window.location.href = payPalURL;
                return;
            }

            if (!paymentOption?.id && !paymentOption?.key && pendingPaymentOptionCreate) {
                try {
                    paymentOption = await paymentoptions.NewPaymentOption({
                        user: getUser(),
                        paymentOption: pendingPaymentOptionCreate.paymentOption,
                        paymentToken: pendingPaymentOptionCreate.paymentToken,
                        creditCardFirst6: pendingPaymentOptionCreate.creditCardFirst6,
                        creditCardLast4: pendingPaymentOptionCreate.creditCardLast4,
                        expireMonth: pendingPaymentOptionCreate.expireMonth,
                        expireYear: pendingPaymentOptionCreate.expireYear,
                        fattmerchantOmniWebPaymentsToken: window.staxOmniWebPaymentsToken,
                        orderIDs: [orderID],
                    });
                    pendingPaymentOptionCreate = undefined;
                } catch (err) {
                    if (paymentOptionFields.mount?.isUnloaded) {
                        paymentOptionFields.mountBlockChyp();
                    }
                    thawCardIframes();
                    hideLoading();
                    showPaymentError(errors.GetErrorMessage(err));
                    return;
                }
            }

            // Charge once — a decline leaves `charged` false so a retry
            // re-attempts only the payment.
            let chargeError: unknown;
            if (!charged) {
                const orderPaymentReq: OrderPaymentsCreateInit = {
                    'user': getUser(),
                    'orderIDs': [orderID],
                    'paymentOptionID': paymentOption?.key || paymentOption?.id,
                };

                if (paymentOption?.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO) {
                    orderPaymentReq['poNumber'] = paymentOptionFields.$poNumber.value;
                    orderPaymentReq['poUploadIDs'] = await poUploader.getUploadIDs();
                }

                payIdemKey ??= crypto.randomUUID();
                orderPaymentReq['idempotencyKey'] = payIdemKey;

                try {
                    await CreateOrderPayments(orderPaymentReq);
                    charged = true;
                } catch (err) {
                    chargeError = err;
                }
            }

            // Hoisted with orderID: a submit can throw AFTER the order exists but
            // BEFORE /checkout-complete fires (merchant-parity remount, a 422 on the
            // Stax exchange). Gating the POST on "created this submit" would skip it
            // on every retry and NodeOrderCheckoutComplete flows would never run for
            // the order. Set only after a successful POST — at-least-once, since the
            // handler fires the node event unconditionally and a lost event is worse
            // than a rare duplicate from a response that failed after the server
            // processed it.
            if (!checkoutCompleteSent) {
                await axios.post(`/orders/${orderID}/checkout-complete`);
                checkoutCompleteSent = true;
            }

            if (chargeError) {
                // A declined charge is a retry-in-place: the order exists and the
                // form is locked, only the payment needs fixing — show it on the
                // payment card like the other brands, not the generic toast. The
                // customer-api returns declines as param errors, so pull the real
                // message out instead of axios's "Request failed with status 400".
                if (paymentOptionFields.mount?.isUnloaded) {
                    paymentOptionFields.mountBlockChyp();
                }
                thawCardIframes();
                hideLoading();
                let chargeErrorText = errors.GetErrorMessage(chargeError);
                const chargeParamErrors = errors.GetAxiosParamErrors(chargeError);
                if (chargeParamErrors?.errors.length) {
                    chargeErrorText = chargeParamErrors.errors.
                        map(e => e.code === 'declined' ? `Payment declined: ${e.message}` : e.message).
                        join(', ');
                }
                showPaymentError(chargeErrorText);
                return;
            }

            await smgCarts.DeleteCart();
            window.location.href = `${Relref('/order-success')}?id=${orderKey}`;
        } catch (err) {
            if (paymentOptionFields.mount?.isUnloaded) {
                paymentOptionFields.mountBlockChyp();
            }
            thawCardIframes();
            hideLoading();
            errors.ShowError(err, form, undefined, {
                'shippingOptionCreateRequest.fullName': 'fullName',
                'shippingOptionCreateRequest.addressLine1': 'addressLine1',
                'shippingOptionCreateRequest.city': 'city',
                'shippingOptionCreateRequest.zip': 'zip',
                'shippingOptionCreateRequest.phone': 'phone',
                'shippingOptionCreateRequest.email': 'email',
            });
        }
    });

    // Pre-fill session data for logged-in users
    if (session.GetLoginToken()) {
        const sess = await session.Session().catch(() => undefined);
        if (sess) {
            const fn = sess.user.fullName();
            if (fn) {
                shippingFullName.value = fn;
                shippingFullName.dispatchEvent(new Event('input'));
            }
            if (sess.user.company) {
                shippingCompany.value = sess.user.company;
                shippingCompany.dispatchEvent(new Event('input'));
            }
            if (sess.user.emailAddress.email) {
                shippingEmail.value = sess.user.emailAddress.email;
                shippingEmail.dispatchEvent(new Event('input'));
            }
            if (sess.user.phoneNumber.phone) {
                await shippingPhone.ready();
                shippingPhone.iti.setNumber(sess.user.phoneNumber.phone);
            }

            // Page saved options 50 at a time until a phone-valid one is found.
            let shippingOptions: models.ShippingOption[] = [];
            for (let page = 0; ; page++) {
                const resp = await axios.get('/shipping-options', {
                    params: {
                        'data': 'address',
                        'sort': 'lastOrdered desc,created desc',
                        'max': 50,
                        'p': page,
                        'q': 'has:email has:phone has:fullName has:addressLine1 has:city has:countryID has:zip',
                    },
                }).catch(() => undefined);
                if (!resp) {
                    break;
                }
                const data = models.ShippingOptionsHyejooResponse.fromJSON(resp.data);
                shippingOptions = data.shippingOptions.filter(shippingOption => phone.IsValid(shippingOption.address?.phoneNumber?.phone ?? ''));
                if (shippingOptions.length || !data.nextPage) {
                    break;
                }
            }

            const savedAddress = shippingOptions[0]?.address;
            if (savedAddress) {
                shippingFullName.value = savedAddress.fullName || '';
                shippingCompany.value = savedAddress.organization || '';
                shippingAddressLine1.value = savedAddress.addressLine1 || '';
                shippingAddressLine2.value = savedAddress.addressLine2 || '';
                shippingCity.value = savedAddress.city || '';
                shippingZip.value = savedAddress.postalCode || '';
                // iti.setNumber avoids a race where setting .value directly on the input can crash
                // intl-tel-input with "null is not an object (r.dialCode)" when utils load before geoip
                await shippingPhone.ready();
                shippingPhone.iti.setNumber(savedAddress.phoneNumber?.phone ?? '');
                shippingEmail.value = savedAddress.emailAddress?.email ?? '';

                if (savedAddress.countryID) {
                    shippingCountry.value = String(savedAddress.countryID);
                    shippingCountry.dispatchEvent(new Event('change'));
                }
                if (savedAddress.stateID) {
                    shippingState.value = String(savedAddress.stateID);
                    shippingState.dispatchEvent(new Event('change'));
                }

                // Dispatch input events to populate shippingOption object
                [
                    shippingFullName,
                    shippingCompany,
                    shippingAddressLine1,
                    shippingAddressLine2,
                    shippingCity,
                    shippingZip,
                    shippingPhoneEl,
                    shippingEmail,
                ].forEach(el => el.dispatchEvent(new Event('input')));
            }

            // Page saved options 50 at a time until a phone-valid one is found.
            let validPaymentOption: models.PaymentOption | undefined;
            for (let page = 0; ; page++) {
                const resp = await axios.get('/payment-options', {
                    params: {
                        'data': 'address,creditCardFirst6,creditCardLast4,creditCardExpirationDate,key,paymentOptionTypeID',
                        'sort': 'lastOrdered desc,created desc',
                        'max': 50,
                        'p': page,
                        'q': 'valid:yes has:email has:phone has:fullName has:addressLine1 has:city has:countryID has:zip',
                    },
                }).catch(() => undefined);
                if (!resp) {
                    break;
                }

                const data = models.PaymentOptionsHyejooResponse.fromJSON(resp.data);
                validPaymentOption = data.paymentOptions.find(paymentOption => phone.IsValid(paymentOption.address.phoneNumber?.phone));
                if (validPaymentOption || !data.nextPage) {
                    break;
                }
            }
            if (validPaymentOption) {
                paymentOption = validPaymentOption;
                drawSavedPaymentOption(paymentOption);
            }
        }
    }

    // Dispatch initial events for non-logged-in users
    shippingCountry.dispatchEvent(new Event('change'));

    // Renders the deposit list once at page load — the deposit orders are fixed
    // for the session; only the applied amount (the server-capped credit) moves
    // with shipping/tax recomputes, and drawCartTotals keeps that header fresh.
    const drawPrepaymentList = (pendingDepositOrders: models.CartPendingDepositOrder[] | undefined) => {
        if (!pendingDepositOrders?.length) {
            return;
        }

        const frag = document.createDocumentFragment();
        for (const depositOrder of pendingDepositOrders) {
            const $row = template.Clone('#prepayment-row-template').firstElementChild as HTMLElement;

            const $invoice = $row.querySelector('.prepayment-row-invoice') as HTMLElement;
            if (depositOrder.invoiceNumber) {
                $invoice.textContent = `Invoice #${depositOrder.invoiceNumber}`;
            } else {
                // Deposit order created but invoice number not yet assigned (rare —
                // Normalize backfills it on insert, but be safe).
                $invoice.textContent = 'Deposit';
            }

            const $date = $row.querySelector('.prepayment-row-date') as HTMLElement;
            $date.textContent = depositOrder.created.isValid ?
                depositOrder.created.toLocaleString({ month: 'short', day: 'numeric', year: 'numeric' }) :
                '';

            ($row.querySelector('.prepayment-row-amount') as HTMLElement).textContent = DollarFormat(depositOrder.paid || 0);
            frag.appendChild($row);
        }
        prepaymentListEl.replaceChildren(frag);

        prepaymentCard.classList.remove('d-none');
    };

    // Load cart items
    const loadCart = async () => {
        const cartIDValue = smgCarts.GetCartID();
        if (!cartIDValue) {
            window.location.href = '/';
            return;
        }

        const cartResp = await carts.GetCart('cartItems,totals,pendingDepositOrders');
        cartItems = cartResp?.cartItems ?? [];
        if (!cartItems.length) {
            window.location.href = '/';
            return;
        }

        subtotal = cartResp?.totals.subtotal ?? Big(0);
        availableDiscount = cartResp?.totals.availableDiscount ?? Big(0);
        shipping = cartResp?.totals.shipping ?? Big(0);
        taxAmount = cartResp?.totals.taxAmount ?? Big(0);
        taxRate = cartResp?.totals.taxRate ?? Big(0);
        prepayment = cartResp?.totals.prepayment ?? Big(0);

        drawPrepaymentList(cartResp?.pendingDepositOrders);

        updateCartTotals();
        drawCartTotals();

        // Render cart items
        const cartSpinner = document.querySelector('#cart-spinner') as HTMLElement;
        const cartItemsContainer = document.querySelector('#cart-items') as HTMLElement;
        const cartItemTemplate = document.querySelector('#cart-item-template') as HTMLTemplateElement;

        cartSpinner.remove();

        for (const cartItem of cartItems) {
            const fragment = cartItemTemplate.content.cloneNode(true) as DocumentFragment;
            const el = fragment.firstElementChild as HTMLElement;

            const title = el.querySelector('.cart-item-title') as HTMLElement;
            const description = el.querySelector('.cart-item-description') as HTMLElement;
            const $proofsContainer = el.querySelector('.cart-item-proofs') as HTMLElement;
            const $proofsList = el.querySelector('.cart-item-proofs-list') as HTMLElement;
            const itemSubtotal = el.querySelector('.cart-item-subtotal') as HTMLElement;

            const addedProofs = new Set<string>();
            const fancyboxGroup = `cart-item-${cartItem.id || ''}`;

            const addProof = (proof: models.Proof, alt: string) => {
                if (!proof.thumbnail || (proof.id && addedProofs.has(proof.id))) {
                    return;
                }
                if (proof.id) {
                    addedProofs.add(proof.id);
                }
                const $a = document.createElement('a');
                $a.href = proof.main || proof.thumbnail;
                $a.setAttribute('data-fancybox', fancyboxGroup);
                $a.setAttribute('data-caption', alt);
                const $img = document.createElement('img');
                $img.src = proof.thumbnail;
                $img.alt = alt;
                $img.style.height = '60px';
                $a.appendChild($img);
                $proofsList.appendChild($a);
            };

            if (cartItem.quote) {
                title.textContent = `Quote ${cartItem.quote.serial || ''}`;
                if (cartItem.order) {
                    const $reorderOf = document.createElement('span');
                    $reorderOf.className = 'small text-muted ms-2';
                    $reorderOf.textContent = `Reorder of ${cartItem.order.invoiceNumber}`;
                    title.after($reorderOf);
                }

                const lineItems = cartItem.quote.quoteLineItems?.filter(li => !li.lineItemModel?.tax && !li.lineItemModel?.shipping) || [];
                const descriptions: string[] = [];
                for (const li of lineItems) {
                    const qty = li.quantity || 0;
                    const product = (qty === 1 ? li.lineItemModel?.name : li.lineItemModel?.pluralName) || li.lineItem;
                    descriptions.push(`${Number(qty).toLocaleString()} ${product || ''}`);

                    if (li.quoteProof?.proof) {
                        addProof(li.quoteProof.proof, `Proof ${li.quoteProof.number || ''}`);
                    }
                }
                description.textContent = descriptions.join(', ');

                // Add quote-level proofs not already shown
                if (cartItem.quote.quoteProofs) {
                    for (const qp of cartItem.quote.quoteProofs) {
                        if (qp.proof) {
                            addProof(qp.proof, `Proof ${qp.number || ''}`);
                        }
                    }
                }
            } else if (cartItem.order) {
                title.textContent = `Reorder ${cartItem.order.invoiceNumber || ''}`;

                const lineItems = cartItem.order.orderLineItems?.filter(li => !li.lineItemModel?.tax && !li.lineItemModel?.shipping) || [];
                const descriptions: string[] = [];
                for (const li of lineItems) {
                    const qty = li.quantity || 0;
                    const product = (qty === 1 ? li.lineItemModel?.name : li.lineItemModel?.pluralName) || li.lineItem;
                    descriptions.push(`${Number(qty).toLocaleString()} ${product || ''}`);

                    if (li.orderProof?.proof) {
                        addProof(li.orderProof.proof, li.orderProof.name || '');
                    }
                }
                description.textContent = descriptions.join(', ');
            }

            // Show attachments section if there are proofs
            if ($proofsList.children.length) {
                $proofsContainer.classList.remove('d-none');
            }

            itemSubtotal.textContent = DollarFormat(cartItem.total || 0);

            cartItemsContainer.appendChild(el);
        }
    };

    await loadCart();

    // Nudge SMG staff to finish the order in IAP instead of the customer checkout
    // (invisible for everyone else). Runs last so it never blocks checkout setup.
    MountStaffNudge({ anchor: form, cartID: smgCarts.GetCartID() || '' });
}
